import React, { useEffect, useState } from "react";
import { Plus, Save, Trash2, PlayCircle, PauseCircle, Sparkles, RefreshCcw, Clock, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import ThumbnailPicker from "../../components/ThumbnailPicker";

/**
 * Tap In Crew admin panel.
 * List/create/edit/delete bot accounts; dial global engagement settings;
 * trigger an immediate run for demo/testing.
 */
export default function CrewAdminTab() {
  const [state, setState] = useState({ crew: [], settings: null, personalities: [] });
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState(false);
  const [form, setForm] = useState({ name: "", handle: "", personality: "hype", bio: "", picture: "" });
  const [busy, setBusy] = useState(false);
  const [moods, setMoods] = useState([]);
  const [currentMood, setCurrentMood] = useState(null);
  const [applyingMood, setApplyingMood] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, sch, mds] = await Promise.all([
        api.get("/admin/crew"),
        api.get("/admin/crew-boost/scheduled").catch(() => ({ data: [] })),
        api.get("/admin/crew/moods").catch(() => ({ data: { presets: [], current_mood: null } })),
      ]);
      setState(c.data);
      setScheduled(sch.data || []);
      setMoods(mds.data?.presets || []);
      setCurrentMood(mds.data?.current_mood || null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(() => {
      // Refresh scheduled boosts only (cheap)
      api.get("/admin/crew-boost/scheduled").then((r) => setScheduled(r.data || [])).catch(() => {});
    }, 7000);
    return () => clearInterval(iv);
  }, []);

  const cancelBoost = async (b) => {
    try {
      await api.delete(`/admin/crew-boost/scheduled/${b.id}`);
      toast.success("Cancelled");
      setScheduled((list) => list.filter((i) => i.id !== b.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.handle.trim()) return toast.error("Name and handle required");
    setBusy(true);
    try {
      await api.post("/admin/crew", form);
      toast.success("Crew member added");
      setForm({ name: "", handle: "", personality: "hype", bio: "", picture: "" });
      setComposer(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const updateBot = async (uid, patch) => {
    try {
      await api.put(`/admin/crew/${uid}`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteBot = async (b) => {
    if (!window.confirm(`Remove ${b.name} from the Tap In Crew?`)) return;
    try {
      await api.delete(`/admin/crew/${b.user_id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveSettings = async (patch) => {
    try {
      const { data } = await api.put("/admin/crew-settings", { ...state.settings, ...patch });
      setState((s) => ({ ...s, settings: data }));
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runNow = async () => {
    try {
      await api.post("/admin/crew/run-now");
      toast.success("Engagement pass triggered");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyMood = async (key) => {
    setApplyingMood(key);
    try {
      const { data } = await api.post(`/admin/crew/moods/${key}/apply`);
      toast.success(`Applied ${data.label} • ${data.bots_reassigned} bots retuned`);
      setCurrentMood(key);
      setState((s) => ({ ...s, settings: data.settings }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplyingMood(null); }
  };

  if (loading) return <div className="text-white/60">Loading crew…</div>;
  const s = state.settings || {};

  return (
    <div data-testid="crew-admin-tab" className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">Tap In Crew</div>
        <h2 className="font-display font-black text-4xl text-white tracking-tighter mt-1">Bot Engagement Control</h2>
        <p className="text-white/60 text-sm mt-2">Spawn crew members, assign personalities, and dial how chatty they are.</p>
      </div>

      {/* Mood Presets */}
      <section data-testid="crew-moods" className="tv-glass rounded-2xl border border-white/10 p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-xl text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-tv-accent" /> Crew mood presets
          </h3>
          {currentMood && (
            <span className="text-xs bg-white/10 text-white/80 px-2.5 py-1 rounded-full" data-testid="crew-current-mood">
              Active: <b className="text-tv-accent uppercase">{currentMood.replace("_", " ")}</b>
            </span>
          )}
        </div>
        <p className="text-white/55 text-xs mt-1.5">One-click bundles for crowd tone + speed. Applying a preset also re-shuffles personalities across all {state.crew?.length || 0} bots.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
          {moods.map((m) => {
            const active = currentMood === m.key;
            return (
              <button
                key={m.key}
                data-testid={`crew-mood-${m.key}`}
                disabled={applyingMood === m.key}
                onClick={() => applyMood(m.key)}
                className={`text-left rounded-xl border p-3 transition ${
                  active
                    ? "border-tv-accent bg-tv-accent/10"
                    : "border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/20"
                } ${applyingMood === m.key ? "opacity-60" : ""}`}
              >
                <div className="text-2xl">{m.emoji}</div>
                <div className="font-display font-bold text-white mt-1 text-sm">{m.label}</div>
                <div className="text-white/55 text-[11px] mt-1 leading-snug">{m.description}</div>
                <div className="mt-2 text-[10px] text-white/45">
                  {m.settings.enabled ? `every ${m.settings.interval_seconds}s · ${m.settings.bots_per_post_min}-${m.settings.bots_per_post_max} bots` : "engagement paused"}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Global settings */}
      <section className="tv-glass rounded-2xl border border-white/10 p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-xl text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-tv-accent" /> Global throttle
          </h3>
          <button data-testid="crew-run-now-btn" onClick={runNow} className="bg-tv-accent hover:bg-yellow-400 text-black font-bold text-xs px-3 py-1.5 rounded-full flex items-center gap-1">
            <RefreshCcw className="w-3 h-3" /> Run pass now
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-5">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold flex items-center justify-between">Engagement loop <input type="checkbox" data-testid="crew-toggle-enabled" checked={s.enabled ?? true} onChange={(e) => saveSettings({ enabled: e.target.checked })} /></span>
            <div className="text-xs mt-2 text-white/70">{s.enabled ? "Running" : "Paused"}</div>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Interval seconds</span>
            <input data-testid="crew-interval-input" type="number" min={15} max={3600} defaultValue={s.interval_seconds || 90} onBlur={(e) => saveSettings({ interval_seconds: parseInt(e.target.value, 10) })} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Window minutes</span>
            <input data-testid="crew-window-input" type="number" min={1} max={720} defaultValue={s.window_minutes || 30} onBlur={(e) => saveSettings({ window_minutes: parseInt(e.target.value, 10) })} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Like probability ({Math.round((s.like_probability ?? 0.85) * 100)}%)</span>
            <input data-testid="crew-like-slider" type="range" min={0} max={1} step={0.05} value={s.like_probability ?? 0.85} onChange={(e) => saveSettings({ like_probability: parseFloat(e.target.value) })} className="w-full mt-2" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Comment probability ({Math.round((s.comment_probability ?? 0.6) * 100)}%)</span>
            <input data-testid="crew-comment-slider" type="range" min={0} max={1} step={0.05} value={s.comment_probability ?? 0.6} onChange={(e) => saveSettings({ comment_probability: parseFloat(e.target.value) })} className="w-full mt-2" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Bots per post (min-max)</span>
            <div className="flex gap-2 mt-1">
              <input data-testid="crew-bots-min" type="number" min={1} max={10} defaultValue={s.bots_per_post_min || 2} onBlur={(e) => saveSettings({ bots_per_post_min: parseInt(e.target.value, 10) })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
              <input data-testid="crew-bots-max" type="number" min={1} max={10} defaultValue={s.bots_per_post_max || 3} onBlur={(e) => saveSettings({ bots_per_post_max: parseInt(e.target.value, 10) })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
            </div>
          </label>
        </div>
      </section>

      {/* Scheduled boosts */}
      <section>
        <h3 className="font-display font-bold text-xl text-white mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-tv-accent" /> Scheduled boosts
          <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold bg-white/5 px-2 py-0.5 rounded-full">{scheduled.filter((b) => b.status === "pending").length} pending</span>
        </h3>
        {scheduled.length === 0 ? (
          <div className="tv-glass rounded-xl border border-white/10 p-5 text-white/50 text-sm">
            No boosts scheduled. Open any live stream and tap the ▾ next to "Crew Boost" to schedule one.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {scheduled.slice(0, 20).map((b) => {
              const fireAt = new Date(b.fire_at);
              const relative = Math.round((fireAt - Date.now()) / 1000);
              const pending = b.status === "pending";
              const tone = pending ? "border-tv-accent/40" : b.status === "fired" ? "border-green-500/30 opacity-75" : "border-white/10 opacity-60";
              return (
                <div key={b.id} data-testid={`scheduled-boost-${b.id}`} className={`tv-glass rounded-xl border ${tone} p-4 flex items-center gap-3`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${pending ? "bg-tv-accent/15" : "bg-white/5"}`}>
                    {b.status === "fired" ? "✓" : b.status === "cancelled" ? "⛔" : "🔥"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white text-sm truncate">{b.stream?.title || b.stream_id.slice(0, 8)}</div>
                    <div className="text-white/50 text-xs">
                      {b.status} · {pending ? (relative > 0 ? `fires in ${relative}s` : "firing…") : new Date(b.fired_at || b.fire_at).toLocaleTimeString()}
                      {" · "}{b.count} bots · {b.duration_seconds}s
                    </div>
                  </div>
                  {pending && (
                    <button data-testid={`scheduled-boost-cancel-${b.id}`} onClick={() => cancelBoost(b)} className="text-red-300 hover:text-red-200 p-1.5" title="Cancel">
                      <XIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Crew roster */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-xl text-white">Crew members ({state.crew.length})</h3>
          <button data-testid="crew-new-btn" onClick={() => setComposer(true)} className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-4 py-2 rounded-full flex items-center gap-1 text-sm">
            <Plus className="w-4 h-4" /> New bot
          </button>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {state.crew.map((b) => (
            <div key={b.user_id} data-testid={`crew-card-${b.user_id}`} className={`tv-glass rounded-xl border p-4 flex gap-3 ${b.bot_active === false ? "border-white/5 opacity-60" : "border-white/10"}`}>
              <img src={b.picture} alt="" className="w-14 h-14 rounded-full object-cover" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-white font-bold truncate">{b.name}</div>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-tv-accent bg-tv-accent/10 px-1.5 rounded">{b.bot_personality || "hype"}</span>
                </div>
                <div className="text-white/40 text-xs">@{b.handle}</div>
                <div className="text-white/60 text-xs mt-1 line-clamp-2">{b.bio}</div>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <select
                    data-testid={`crew-personality-${b.user_id}`}
                    value={b.bot_personality || "hype"}
                    onChange={(e) => updateBot(b.user_id, { personality: e.target.value })}
                    className="bg-[#0B1120] border border-white/10 rounded px-2 py-1 text-xs text-white"
                  >
                    {state.personalities.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <button
                    data-testid={`crew-toggle-active-${b.user_id}`}
                    onClick={() => updateBot(b.user_id, { active: b.bot_active === false })}
                    className={`text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 ${b.bot_active === false ? "bg-white/10 text-white/70" : "bg-green-500/20 text-green-300"}`}
                  >
                    {b.bot_active === false ? <><PlayCircle className="w-3 h-3" /> Activate</> : <><PauseCircle className="w-3 h-3" /> Pause</>}
                  </button>
                  <button
                    data-testid={`crew-delete-${b.user_id}`}
                    onClick={() => deleteBot(b)}
                    className="text-xs text-red-300 hover:text-red-200 px-2 py-1 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {composer && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4">
          <form onSubmit={submit} data-testid="crew-composer" className="bg-[#0B1120] border border-white/10 rounded-2xl p-6 w-full max-w-xl space-y-3 relative">
            <button type="button" onClick={() => setComposer(false)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center">✕</button>
            <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">New crew member</div>
            <h3 className="font-display font-black text-3xl text-white tracking-tighter">Spawn a bot</h3>

            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Display name *</span>
              <input data-testid="crew-new-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">@handle *</span>
              <input data-testid="crew-new-handle" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 24) })} required className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Personality</span>
              <select data-testid="crew-new-personality" value={form.personality} onChange={(e) => setForm({ ...form, personality: e.target.value })} className="w-full mt-1 bg-[#0B1120] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                {state.personalities.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Bio</span>
              <textarea data-testid="crew-new-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={2} maxLength={600} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent resize-none" />
            </label>
            <ThumbnailPicker
              value={form.picture}
              onChange={(url) => setForm({ ...form, picture: url })}
              testId="crew-new-avatar"
              label="Avatar (optional)"
            />

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setComposer(false)} className="text-white/70 px-4 py-2 text-sm">Cancel</button>
              <button disabled={busy} data-testid="crew-new-submit" className="bg-tv-accent text-black font-bold px-5 py-2 rounded-full text-sm disabled:opacity-60">
                {busy ? "Creating…" : "Add member"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
